import {createSlice, nanoid, PayloadAction} from '@reduxjs/toolkit';
import path from 'path';
import slash from 'slash';
import {
  encompassFileFormat,
  imageListFormats,
  lasFileFormat,
  orthoFormats,
  planarListFormats
} from '../asset-utils';

export interface CameraData {
  x: number;
  y: number;
  z: number;
  roll: number;
  pitch: number;
  yaw: number;
  path: string;
  name: string;
  config?: object
}

interface PointCloudData {
  path: string;
}

export interface OrthoData {
  imageBounds: [];
  imageData: string;
  imageSize: [number, number];
  imageSizeOriginal: [number, number];
  imagePath: string;
  scaleX: number,
  scaleY: number,
}

export interface Asset {
  id: string;
  name: string;
  fromJSON: boolean;
  date: string;
  visible: boolean;
  folderID: string;
  type: AssetType;
  data: PointCloudData | CameraData[] | OrthoData;
}

export interface AssetListItem {
  folderID: string,
  assetPath: string
  data?: CameraData[] | OrthoData;
}

// NOTE: Always add new asset types to bottom of list
export enum AssetType {
  LAS,
  Encompass,
  Panoramic,
  Unknown,
  OrthoMosaic,
  Planar
}

export const assetTypeFromExtension = (ext: string): AssetType => {
  if (ext === lasFileFormat) {
    return AssetType.LAS;
  }

  if (ext === encompassFileFormat) {
    return AssetType.Encompass;
  }

  if (imageListFormats.indexOf(ext) !== -1) {
    return AssetType.Panoramic;
  }

  if (planarListFormats.indexOf(ext) !== -1) {
    return AssetType.Planar;
  }

  if (orthoFormats.indexOf(ext) !== -1) {
    return AssetType.OrthoMosaic;
  }

  return AssetType.Unknown;
};

const getAssetPayload = (folderID: string, assetPath: string, data?: CameraData[] | OrthoData): Asset => {
  assetPath = slash(assetPath);
  const extension = path.extname(assetPath).slice(1);

  let assetData;

  if (data) {
    assetData = data;
  } else {
    assetData = {
      path: extension === encompassFileFormat
        ? path.dirname(assetPath)
        : assetPath
    };
  }

  const payload = {
    id: nanoid(),
    name: path.basename(assetPath),
    date: new Date().toISOString(),
    type: assetTypeFromExtension(extension),
    folderID,
    visible: true,
    fromJSON: false,
    data: assetData
  } as Asset;

  return payload;
};

export const assetsSlice = createSlice({
  name: 'assets',
  initialState: [] as Asset[],
  reducers: {
    addAsset: {
      reducer: (state, action: PayloadAction<Asset>) => {
        state.push(action.payload);
      },
      prepare: (folderID: string, assetPath: string, data?: CameraData[] | OrthoData): {payload: Asset} => {
        const payload = getAssetPayload(folderID, assetPath, data);
        return {payload};
      }
    },
    addAssets: {
      /** Add multiple assets at once. Much faster than adding single assets */
      reducer: (state, action: PayloadAction<Asset[]>) => {
        action.payload.forEach(payload => {
          state.push(payload);
        });
      },
      prepare: (assetsList: AssetListItem[]): {payload: Asset[]} => {
        const payload = assetsList.map(assetItem => {
          const {folderID, assetPath, data} = assetItem;
          return getAssetPayload(folderID, assetPath, data);
        });

        return {payload};
      }
    },
    changeFolder: (state, action: PayloadAction<{folderID: string, assetID: string}>) => {
      const {assetID, folderID} = action.payload;
      const asset = state.find(asset => asset.id === assetID);

      if (asset) {
        asset.folderID = folderID;
      }
    },
    changeName: (state, action) => {
      const {assetID, name} = action.payload;
      const asset = state.find(asset => asset.id === assetID);

      if (asset) {
        asset.name = name;
      }
    },
    toggleVisibility: (state, action) => {
      const assetID = action.payload;
      const asset = state.find(asset => asset.id === assetID);

      if (asset) {
        asset.visible = !asset.visible;
      }
    },
    toggleFolderVisibility: (state, action) => {
      const {folderID, visible} = action.payload;
      const assets = state.filter(asset => asset.folderID === folderID);

      assets.forEach(asset => {
        asset.visible = visible;
      });
    },
    deleteAsset: (state, action) =>
      state.filter(asset => asset.id !== action.payload),
    deleteAssets: (state, action) =>
      state.filter(asset => asset.folderID !== action.payload)
  }
});

export default assetsSlice.reducer;

export const {
  addAsset,
  addAssets,
  changeFolder,
  toggleVisibility,
  toggleFolderVisibility,
  changeName,
  deleteAsset,
  deleteAssets,
} = assetsSlice.actions;

export const selectAllAssets = state =>
  state.assets;

export const getPointCloudAssets = assets =>
  assets.filter(asset => asset.type === AssetType.Encompass || asset.type === AssetType.LAS);

export const getCameraFileAssets = assets =>
  assets.filter(asset => asset.type === AssetType.Panoramic || asset.type === AssetType.Planar);

export const getOrthoAssets = assets =>
  assets.filter(asset => asset.type === AssetType.OrthoMosaic);

export const selectAssetsByFolderID = folderID => state =>
  state.assets.filter(asset => asset.folderID === folderID);

export const selectAssetByID = assetID => state =>
  state.assets.find(asset => asset.id === assetID);